/**
 * Meniuri — punctul în care catalogul devine un canal de afișare.
 *
 * Fiecare meniu este o selecție din catalog, publicată ca versiune imutabilă. Publicarea este
 * momentul în care modificările ajung la clienți: QR-urile tipărite NU se schimbă niciodată,
 * iar ecranele primesc noua versiune și își reîncarcă conținutul automat.
 */
import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  cx,
  useToast,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { formatDateTime, relativeTime, useResource } from '../lib/hooks.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';

interface Menu {
  id: string;
  public_id: string;
  name: string;
  description: string | null;
  kind: string;
  status: 'draft' | 'published' | 'archived';
  published_at: string | null;
  updated_at: string;
  restaurant_id: string;
  restaurant_name: string;
  /** Adresa publică: /r/{restaurant_slug}/{menu_slug} — vezi cardul meniului. */
  slug: string;
  restaurant_slug: string;
  is_public: boolean;
  items: number;
  qr_codes: number;
  /** view_only = doar afișare · dine_in = comenzi la masă · online = ridicare/livrare. */
  ordering_mode: 'view_only' | 'dine_in' | 'online';
}

interface Restaurant {
  id: string;
  name: string;
  currency: string;
}

/**
 * Modul de comandă este cea mai importantă setare a unui meniu: același meniu poate fi doar de
 * citit pe o tabletă de prezentare, cu comandă la masă, sau cu ridicare și livrare online.
 * Textele de aici sunt exact cele pe care le vede clientul în meniul public.
 */
const ORDERING_META: Record<Menu['ordering_mode'], { label: string; badge: string; hint: string }> = {
  view_only: {
    label: 'Doar vizualizare',
    badge: 'fără comandă',
    hint: 'Clienții văd meniul, dar nu pot comanda. Potrivit pentru meniuri de prezentare sau evenimente.',
  },
  dine_in: {
    label: 'Comandă la masă',
    badge: 'la masă',
    hint: 'Se comandă scanând codul de pe masă; masa vine din cod, iar plata se face la restaurant.',
  },
  online: {
    label: 'Ridicare și livrare',
    badge: 'online',
    hint: 'Coș cu ridicare personală sau livrare la adresă, cu taxă și prag minim din setările de comenzi.',
  },
};

// Etichetele corespund exact valorilor acceptate de API (aceleași ca în baza de date).
const KIND_LABELS: Record<string, string> = {
  food: 'Meniu principal',
  events: 'Evenimente',
  custom: 'Personalizat',
  breakfast: 'Mic dejun',
  lunch: 'Prânz',
  dinner: 'Cină',
  drinks: 'Băuturi',
  desserts: 'Deserturi',
  kids: 'Copii',
  seasonal: 'Sezonier',
  bar: 'Bar',
};

const STATUS_META: Record<Menu['status'], { label: string; tone: 'neutral' | 'success' | 'warning' }> = {
  draft: { label: 'ciornă', tone: 'warning' },
  published: { label: 'publicat', tone: 'success' },
  archived: { label: 'arhivat', tone: 'neutral' },
};

export function MenusPage() {
  const session = useSession();
  const toast = useToast();
  const menus = useResource<Menu[]>(() => api.get<Menu[]>('/menus'), [session.activeAccountId]);
  const restaurants = useResource<Restaurant[]>(() => api.get<Restaurant[]>('/restaurants'), [session.activeAccountId]);

  const [createOpen, setCreateOpen] = useState(false);
  const [publishing, setPublishing] = useState<Menu | null>(null);
  const [updatingMode, setUpdatingMode] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Menu | null>(null);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [form, setForm] = useState({
    name: '',
    description: '',
    kind: 'food',
    restaurantId: '',
    orderingMode: 'view_only' as Menu['ordering_mode'],
  });

  const canCreate = session.can('menu.create');
  const canPublish = session.can('menu.publish');
  const canDelete = session.can('menu.delete');
  // Modul de comandă afectează banii și bucătăria, deci cere un drept separat (order.settings).
  const canOrderSettings = session.can('order.settings');

  const published = (menus.data ?? []).filter((menu) => menu.status === 'published');
  const drafts = (menus.data ?? []).filter((menu) => menu.status !== 'published');

  async function create() {
    setBusy(true);
    setErrors({});
    try {
      await api.post('/menus', {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        kind: form.kind,
        restaurantId: form.restaurantId || restaurants.data?.[0]?.id,
        orderingMode: form.orderingMode,
      });
      toast.success('Meniul a fost creat', 'Adaugă produse din catalog, apoi publică-l pentru a-l face vizibil.');
      setCreateOpen(false);
      setForm({ name: '', description: '', kind: 'food', restaurantId: '', orderingMode: 'view_only' });
      await menus.reload();
    } catch (error) {
      const fields = fieldErrors(error);
      setErrors(fields);
      toast.error('Meniul nu a putut fi creat', Object.keys(fields).length ? Object.values(fields)[0]?.[0] : errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Schimbarea modului de comandă se salvează imediat, fără buton de „salvează": managerul care
   * oprește livrarea în timpul serviciului trebuie să vadă efectul în meniul public imediat.
   */
  async function changeOrderingMode(menu: Menu, mode: Menu['ordering_mode']) {
    if (mode === menu.ordering_mode) return;
    setUpdatingMode(menu.id);
    try {
      await api.patch(`/menus/${menu.id}`, { orderingMode: mode });
      toast.success(
        `„${menu.name}” — ${ORDERING_META[mode].label.toLowerCase()}`,
        mode === 'view_only' ? 'Coșul dispare din meniul public.' : ORDERING_META[mode].hint,
      );
      await menus.reload();
    } catch (caught) {
      toast.error('Nu am putut schimba modul de comandă', errorMessage(caught));
    } finally {
      setUpdatingMode(null);
    }
  }

  async function publish() {
    if (!publishing) return;
    setBusy(true);
    try {
      const result = await api.post<{ version: number; items: number; sections: number }>(`/menus/${publishing.id}/publish`);
      toast.success(
        `„${publishing.name}” a fost publicat (versiunea ${result.version})`,
        `${result.items} produse sunt acum live pe QR, meniul web și ecrane. QR-urile tipărite rămân valabile.`,
      );
      setPublishing(null);
      await menus.reload();
    } catch (error) {
      toast.error('Publicarea a eșuat', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.del(`/menus/${deleting.id}`);
      toast.success(`„${deleting.name}” a fost șters`);
      setDeleting(null);
      await menus.reload();
    } catch (error) {
      toast.error('Meniul nu a putut fi șters', errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page-enter">
      <PageHeader
        title="Meniuri"
        subtitle="Selecții din catalogul central, publicate ca versiuni. Un produs modificat se reflectă în toate meniurile care îl conțin."
        actions={
          canCreate ? (
            <Button
              icon={<Icon name="plus" size={17} />}
              onClick={() => {
                setForm({
                  name: '',
                  description: '',
                  kind: 'food',
                  restaurantId: restaurants.data?.[0]?.id ?? '',
                  orderingMode: 'view_only',
                });
                setErrors({});
                setCreateOpen(true);
              }}
            >
              Meniu nou
            </Button>
          ) : null
        }
      />

      {menus.loading ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-44" />
          ))}
        </div>
      ) : menus.error ? (
        <InlineAlert tone="warning" title="Meniurile nu au putut fi încărcate">
          {menus.error}{' '}
          <button type="button" className="underline" onClick={() => void menus.reload()}>
            Reîncearcă
          </button>
        </InlineAlert>
      ) : !menus.data?.length ? (
        <EmptyState
          icon={<Icon name="menu" size={26} />}
          title="Niciun meniu încă"
          description="Un meniu este o selecție din catalog pe care o publici către QR, site și ecrane. Poți avea meniu principal, de mic dejun, de bar sau sezonier."
          action={canCreate ? <Button onClick={() => setCreateOpen(true)}>Creează primul meniu</Button> : null}
        />
      ) : (
        <div className="flex flex-col gap-8">
          {published.length ? (
            <section>
              <h2 className="mb-3 text-body-s font-medium tracking-wide text-ink-400 uppercase">Publicate</h2>
              <div className="grid gap-4 lg:grid-cols-2">
                {published.map((menu) => (
                  <MenuCard
                    key={menu.id}
                    menu={menu}
                    canPublish={canPublish}
                    canDelete={canDelete}
                    canOrderSettings={canOrderSettings}
                    orderingBusy={updatingMode === menu.id}
                    onPublish={() => setPublishing(menu)}
                    onDelete={() => setDeleting(menu)}
                    onOrderingMode={(mode) => void changeOrderingMode(menu, mode)}
                  />
                ))}
              </div>
            </section>
          ) : null}

          {drafts.length ? (
            <section>
              <h2 className="mb-3 text-body-s font-medium tracking-wide text-ink-400 uppercase">Ciorne și arhivate</h2>
              <div className="grid gap-4 lg:grid-cols-2">
                {drafts.map((menu) => (
                  <MenuCard
                    key={menu.id}
                    menu={menu}
                    canPublish={canPublish}
                    canDelete={canDelete}
                    canOrderSettings={canOrderSettings}
                    orderingBusy={updatingMode === menu.id}
                    onPublish={() => setPublishing(menu)}
                    onDelete={() => setDeleting(menu)}
                    onOrderingMode={(mode) => void changeOrderingMode(menu, mode)}
                  />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Meniu nou"
        description="După creare, adaugi produse din catalog și publici meniul către canale."
        footer={
          <>
            <Button variant="secondary" disabled={busy} onClick={() => setCreateOpen(false)}>
              Anulează
            </Button>
            <Button loading={busy} disabled={form.name.trim().length < 2} onClick={() => void create()}>
              Creează meniul
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Field label="Nume meniu" required errors={errors.name}>
            {(props) => (
              <Input
                {...props}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="Meniu principal"
              />
            )}
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Tip meniu">
              {(props) => (
                <Select {...props} value={form.kind} onChange={(event) => setForm({ ...form, kind: event.target.value })}>
                  {Object.entries(KIND_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Restaurant">
              {(props) => (
                <Select {...props} value={form.restaurantId} onChange={(event) => setForm({ ...form, restaurantId: event.target.value })}>
                  {(restaurants.data ?? []).map((restaurant) => (
                    <option key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <Field
            label="Cum se comandă din acest meniu"
            hint={ORDERING_META[form.orderingMode].hint}
          >
            {(props) => (
              <Select
                {...props}
                value={form.orderingMode}
                onChange={(event) => setForm({ ...form, orderingMode: event.target.value as Menu['ordering_mode'] })}
              >
                {Object.entries(ORDERING_META).map(([value, meta]) => (
                  <option key={value} value={value}>
                    {meta.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Descriere" hint="Opțional — apare în capul meniului public.">
            {(props) => (
              <Textarea
                {...props}
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                rows={2}
              />
            )}
          </Field>
        </div>
      </Modal>

      <ConfirmDialog
        open={Boolean(publishing)}
        title={`Publici „${publishing?.name ?? ''}”?`}
        description={`Versiunea publicată devine vizibilă imediat pe toate canalele care folosesc acest meniu: ${publishing?.qr_codes ?? 0} coduri QR, meniul web și ecranele. Codurile QR tipărite rămân aceleași.`}
        confirmLabel="Publică acum"
        tone="primary"
        loading={busy}
        onClose={() => setPublishing(null)}
        onConfirm={() => void publish()}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        title="Ștergi meniul?"
        description={`„${deleting?.name ?? ''}” va fi scos din uz. Codurile QR care îl folosesc trebuie dezactivate mai întâi, ca să nu rămână linkuri moarte.`}
        confirmLabel="Șterge meniul"
        tone="danger"
        loading={busy}
        onClose={() => setDeleting(null)}
        onConfirm={() => void remove()}
      />
    </div>
  );
}

/**
 * Adresa publică a unui meniu: `/r/{restaurantSlug}/{menuSlug}`.
 *
 * Este linkul care se trimite mai departe (client, partener, agenție) și care se poate pune în
 * bio-ul rețelelor sociale. Nu conține meniul, ci doar arată spre el — deci rămâne același
 * după fiecare modificare de preț sau de produs, exact ca un cod QR tipărit.
 */
function publicMenuUrl(menu: Menu): string {
  return `${window.location.origin}/r/${menu.restaurant_slug}/${menu.slug}`;
}

/** Copiază linkul în clipboard, cu fallback pentru browsere fără API de clipboard. */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const area = document.createElement('textarea');
      area.value = text;
      area.style.position = 'fixed';
      area.style.opacity = '0';
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(area);
      return ok;
    } catch {
      return false;
    }
  }
}

function MenuCard({
  menu,
  canPublish,
  canDelete,
  canOrderSettings,
  orderingBusy,
  onPublish,
  onDelete,
  onOrderingMode,
}: {
  menu: Menu;
  canPublish: boolean;
  canDelete: boolean;
  canOrderSettings: boolean;
  orderingBusy: boolean;
  onPublish: () => void;
  onDelete: () => void;
  onOrderingMode: (mode: Menu['ordering_mode']) => void;
}) {
  const status = STATUS_META[menu.status];
  const toast = useToast();
  return (
    <Card className="flex flex-col gap-4">
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            {menu.name}
            <Badge tone={status.tone}>{status.label}</Badge>
          </span>
        }
        subtitle={`${KIND_LABELS[menu.kind] ?? menu.kind} · ${menu.restaurant_name}`}
        /* Modul de comandă se schimbă în timpul serviciului: „azi nu mai facem livrare" trebuie să
           fie la un click, fără să editezi meniul sau să-l republici. */
        action={
          canDelete ? (
            <Button variant="ghost" size="sm" className="text-danger" onClick={onDelete}>
              Șterge
            </Button>
          ) : null
        }
      />

      {menu.description ? <p className="text-body-s text-ink-600">{menu.description}</p> : null}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-ink-100 bg-surface-2 p-3">
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-2 text-body-s font-medium text-ink-800">
            <Icon name="receipt" size={16} />
            {ORDERING_META[menu.ordering_mode]?.label ?? menu.ordering_mode}
            {menu.ordering_mode !== 'view_only' ? (
              <Badge tone="brand">{ORDERING_META[menu.ordering_mode]?.badge}</Badge>
            ) : null}
          </span>
          <span className="text-caption text-ink-400">{ORDERING_META[menu.ordering_mode]?.hint}</span>
        </div>
        {canOrderSettings ? (
          <Select
            aria-label={`Modul de comandă pentru ${menu.name}`}
            value={menu.ordering_mode}
            onChange={(event) => onOrderingMode(event.target.value as Menu['ordering_mode'])}
            className="max-w-[220px]"
            disabled={orderingBusy}
          >
            {Object.entries(ORDERING_META).map(([value, meta]) => (
              <option key={value} value={value}>
                {meta.label}
              </option>
            ))}
          </Select>
        ) : null}
      </div>

      <div className="grid grid-cols-3 gap-3 border-y border-ink-100 py-3">
        <div className="flex flex-col">
          <span className="text-caption text-ink-400">Produse</span>
          <span className="text-body-l text-ink-950 nums">{menu.items}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-caption text-ink-400">Coduri QR</span>
          <span className="text-body-l text-ink-950 nums">{menu.qr_codes}</span>
        </div>
        <div className="flex flex-col">
          <span className="text-caption text-ink-400">{menu.published_at ? 'Publicat' : 'Actualizat'}</span>
          <span className="text-body-s text-ink-600">
            {menu.published_at ? relativeTime(menu.published_at) : relativeTime(menu.updated_at)}
          </span>
        </div>
      </div>

      {/* Linkul public: fiecare meniu publicat are propria adresă, nu doar restaurantul. */}
      {menu.status === 'published' && menu.is_public ? (
        <div className="flex flex-col gap-2 rounded-lg border border-ink-100 bg-surface-2 p-3">
          <span className="text-caption text-ink-400">Link public (se poate trimite clienților)</span>
          <div className="flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md bg-surface-1 px-2 py-1.5 font-mono text-caption text-ink-700">
              {publicMenuUrl(menu)}
            </code>
            <Button
              variant="ghost"
              size="sm"
              icon={<Icon name="copy" size={15} />}
              onClick={async () => {
                const ok = await copyToClipboard(publicMenuUrl(menu));
                if (ok) toast.success('Link copiat', 'Îl poți lipi în mesaj, e-mail sau în bio.');
                else toast.error('Nu am putut copia linkul', 'Selectează-l manual din câmpul de mai sus.');
              }}
            >
              Copiază
            </Button>
            <a
              href={publicMenuUrl(menu)}
              target="_blank"
              rel="noreferrer"
              className="rounded-md px-2 py-1.5 text-body-s font-medium text-brand-600 transition-ui hover:bg-brand-50"
            >
              Deschide
            </a>
          </div>
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-ink-200 p-3 text-caption text-ink-400">
          Linkul public se activează după publicare: {menu.items === 0
            ? 'adaugă produse, apoi publică meniul.'
            : 'publică meniul, iar adresa devine /r/' + menu.restaurant_slug + '/' + menu.slug + '.'}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-caption text-ink-400">
          {menu.published_at ? `Ultima publicare: ${formatDateTime(menu.published_at)}` : 'Nepublicat încă'}
        </span>
        <div className="flex items-center gap-2">
          {menu.items === 0 ? (
            <span className="text-caption text-warning">adaugă produse înainte de publicare</span>
          ) : null}
          {canPublish ? (
            <Button
              variant={menu.status === 'published' ? 'secondary' : 'primary'}
              size="sm"
              icon={<Icon name="check" size={15} />}
              onClick={onPublish}
              disabled={menu.items === 0}
            >
              {menu.status === 'published' ? 'Republică' : 'Publică'}
            </Button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}
